import apiClient from '@/shared/services/api/apiClient';
import { extractErrorMessage } from '@/shared/utils/errorHandler';

import { normalizeUser } from '../utils/normalizeUser';

import type { AuthUser } from '../types/auth';

export interface AuthServiceResult {
  user: AuthUser;
  token: string;
}

export const login = async (
  email: string,
  password: string
): Promise<AuthServiceResult> => {
  try {
    const response = await apiClient.post(
      '/auth/login',
      {
        email,
        password,
      },
      { showLoader: true } as any
    );

    const data = response.data?.data;

    const user = normalizeUser(data?.user);
    const token = data?.accessToken;

    if (!user || !token) {
      throw new Error(
        'Invalid response from server'
      );
    }

    return {
      user,
      token,
    };
  } catch (error) {
    throw new Error(
      extractErrorMessage(
        error,
        'Login failed'
      )
    );
  }
};

export const signup = async (
  email: string,
  password: string,
  name: string
): Promise<AuthServiceResult> => {
  try {
    const response = await apiClient.post(
      '/candidate-auth/signup',
      {
        email,
        password,
        name,
      }
    );

    const data = response.data?.data;

    const user = normalizeUser(data?.user);
    const token = data?.accessToken;

    if (!user || !token) {
      throw new Error(
        'Invalid response from server'
      );
    }

    return {
      user,
      token,
    };
  } catch (error) {
    throw new Error(
      extractErrorMessage(
        error,
        'Registration failed'
      )
    );
  }
};

export const forgotPassword = async (
  email: string
): Promise<void> => {
  try {
    await apiClient.post(
      '/auth/forgot-password',
      {
        email,
      }
    );
  } catch (error) {
    throw new Error(
      extractErrorMessage(
        error,
        'Password reset request failed'
      )
    );
  }
};

export const resetPassword = async (
  token: string,
  password: string
): Promise<void> => {
  try {
    await apiClient.post(
      '/auth/reset-password',
      {
        token,
        password,
      }
    );
  } catch (error) {
    throw new Error(
      extractErrorMessage(
        error,
        'Password reset failed'
      )
    );
  }
};

export const signout = async (): Promise<void> => {
  try {
    await apiClient.post(
      '/auth/logout'
    );
  } catch (error) {
    throw new Error(
      extractErrorMessage(
        error,
        'Logout failed'
      )
    );
  }
};